import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#bindGalleryProgress();
  }

  /**
   * Syncs the gallery progress bar with the scroller position.
   */
  #bindGalleryProgress() {
    const bars = this.querySelectorAll('[data-gallery-progress]');
    const scroller = this.slideshow?.refs?.scroller ?? this.querySelector('slideshow-slides');
    if (!bars.length || !scroller) return;

    const isRtl = getComputedStyle(scroller).direction === 'rtl';

    const update = () => {
      const max = scroller.scrollWidth - scroller.clientWidth;
      // In RTL, all current browsers report scrollLeft as 0 at the start (right edge)
      // going down to -max at the end (left edge), so flip the sign to normalize to 0 -> max.
      const scrollLeft = isRtl ? -scroller.scrollLeft : scroller.scrollLeft;

      const ratio = max > 0 ? Math.min(1, Math.max(0, scrollLeft / max)) : 0;
      const value = ratio.toFixed(4);

      bars.forEach((bar) => {
        bar.style.setProperty('--progress', value);
        const count = Number.parseInt(bar.style.getPropertyValue('--count'), 10) || 1;
        bar.setAttribute('aria-valuenow', String(Math.round(ratio * Math.max(count - 1, 0))));
      });
    };

    scroller.addEventListener('scroll', update, { passive: true, signal: this.#controller.signal });
    update();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
